/* Problem Identifier v16 - product metafield source with View More toggle */
(() => {
  class ProblemIdentifier {
    constructor(root) {
      this.root = root;
      this.problems = this.parseJson('[data-pi-problems-json]', []);
      this.settings = this.parseJson('[data-pi-settings-json]', {});
      this.selectedProblemHandle = this.getInitialProblemHandle();
      this.selectedLawnTypes = [];
      this.notSureSelected = false;
      this.searchTerm = '';

      this.problemList = root.querySelector('[data-pi-problem-list]');
      this.searchInput = root.querySelector('[data-pi-search]');
      this.selectedWrap = root.querySelector('[data-pi-selected-wrap]');
      this.results = root.querySelector('[data-pi-results]');
      this.solutionPool = root.querySelector('[data-pi-solution-pool]');
      this.faq = root.querySelector('[data-pi-faq]');
      this.lawnButtons = Array.from(root.querySelectorAll('[data-pi-lawn-type]'));
      this.notSureButton = root.querySelector('[data-pi-not-sure]');
      this.prevButton = root.querySelector('[data-pi-slider-prev]');
      this.nextButton = root.querySelector('[data-pi-slider-next]');
      this.imageDialog = root.querySelector('[data-pi-image-dialog]');
      this.imageDialogImg = root.querySelector('[data-pi-image-dialog-img]');
      this.imageDialogClose = root.querySelector('[data-pi-image-close]');

      if (!this.problems.length) {
        this.renderNoProblems();
        return;
      }

      this.bindEvents();
      this.render();
    }

    parseJson(selector, fallback) {
      const node = this.root.querySelector(selector);
      if (!node) return fallback;
      try {
        return JSON.parse(node.textContent.trim());
      } catch (error) {
        console.warn('Problem Identifier JSON error:', error);
        return fallback;
      }
    }

    getInitialProblemHandle() {
      const params = new URLSearchParams(window.location.search);
      const fromUrl = params.get('problem');
      if (fromUrl && this.problems.some((problem) => problem.handle === fromUrl)) return fromUrl;

      if (this.settings.defaultProblemHandle && this.problems.some((problem) => problem.handle === this.settings.defaultProblemHandle)) {
        return this.settings.defaultProblemHandle;
      }

      return this.problems[0] && this.problems[0].handle;
    }

    bindEvents() {
      if (this.searchInput) {
        this.searchInput.addEventListener('input', (event) => {
          this.searchTerm = event.target.value.trim().toLowerCase();
          this.renderProblemCards();
        });
      }

      if (this.problemList) {
        this.problemList.addEventListener('click', (event) => {
          const button = event.target.closest('[data-pi-problem-card]');
          if (!button) return;
          this.selectProblem(button.dataset.problemHandle, true);
        });
      }

      this.lawnButtons.forEach((button) => {
        button.addEventListener('click', () => {
          this.notSureSelected = false;
          if (this.notSureButton) this.notSureButton.classList.remove('is-active');

          const lawnType = button.dataset.piLawnType;
          if (this.selectedLawnTypes.includes(lawnType)) {
            this.selectedLawnTypes = this.selectedLawnTypes.filter((value) => value !== lawnType);
          } else {
            this.selectedLawnTypes.push(lawnType);
          }

          this.syncLawnButtons();
          this.renderSolutions();
        });
      });

      if (this.notSureButton) {
        this.notSureButton.addEventListener('click', () => {
          this.selectedLawnTypes = [];
          this.notSureSelected = !this.notSureSelected;
          this.syncLawnButtons();
          this.notSureButton.classList.toggle('is-active', this.notSureSelected);
          this.renderSolutions();
        });
      }

      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => this.scrollProblems(-1));
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => this.scrollProblems(1));
      }

      if (this.imageDialogClose) {
        this.imageDialogClose.addEventListener('click', () => this.closeImageDialog());
      }

      if (this.imageDialog) {
        this.imageDialog.addEventListener('click', (event) => {
          if (event.target === this.imageDialog) this.closeImageDialog();
        });
      }

      if (this.results) {
        this.results.addEventListener('click', (event) => {
          const button = event.target.closest('[data-pi-products-toggle]');
          if (!button) return;

          const group = button.closest('[data-pi-products-group]');
          if (!group) return;

          const extraCards = Array.from(group.querySelectorAll('.pi-product-card.is-extra-product'));
          const isExpanded = button.getAttribute('aria-expanded') === 'true';
          const extraCount = Number(button.dataset.extraCount || extraCards.length);

          extraCards.forEach((card) => {
            card.hidden = isExpanded;
          });

          button.setAttribute('aria-expanded', String(!isExpanded));
          button.textContent = isExpanded
            ? `View ${extraCount} more product${extraCount === 1 ? '' : 's'}`
            : 'Show fewer products';
        });
      }

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.closeImageDialog();
      });
    }

    render() {
      this.renderProblemCards();
      this.renderSelectedProblem();
      this.syncLawnButtons();
      this.renderSolutions();
      this.renderFaq();
    }

    filteredProblems() {
      if (!this.searchTerm) return this.problems;
      return this.problems.filter((problem) => {
        const haystack = [
          problem.name,
          problem.scientificName,
          problem.category,
          problem.lifeCycle,
          problem.shortDescription,
          problem.searchTerms
        ].filter(Boolean).join(' ').toLowerCase();

        return haystack.includes(this.searchTerm);
      });
    }

    renderProblemCards() {
      if (!this.problemList) return;
      const problems = this.filteredProblems();

      if (!problems.length) {
        this.problemList.innerHTML = '<p class="pi-no-results">No problems match your search.</p>';
        return;
      }

      this.problemList.innerHTML = problems.map((problem) => {
        const active = problem.handle === this.selectedProblemHandle ? ' is-active' : '';
        const image = problem.image
          ? `<img src="${this.escapeAttr(problem.image)}" alt="${this.escapeAttr(problem.imageAlt || problem.name)}" loading="lazy">`
          : `<span class="pi-problem-card__placeholder">${this.escapeHtml((problem.name || '?').slice(0, 1))}</span>`;

        return `
          <button class="pi-problem-card${active}" type="button" data-pi-problem-card data-problem-handle="${this.escapeAttr(problem.handle)}" aria-pressed="${problem.handle === this.selectedProblemHandle}">
            ${image}
            <span class="pi-problem-card__name">${this.escapeHtml(problem.name)}</span>
            <span class="pi-problem-card__check" aria-hidden="true">✓</span>
          </button>
        `;
      }).join('');
    }

    selectProblem(handle, shouldScroll) {
      if (!this.problems.some((problem) => problem.handle === handle)) return;
      this.selectedProblemHandle = handle;
      this.updateUrl(handle);
      this.renderProblemCards();
      this.renderSelectedProblem();
      this.renderSolutions();
      this.renderFaq();

      if (shouldScroll && this.selectedWrap) {
        const top = this.selectedWrap.getBoundingClientRect().top + window.scrollY - 90;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    }

    updateUrl(handle) {
      const url = new URL(window.location.href);
      url.searchParams.set('problem', handle);
      window.history.replaceState({}, '', url.toString());
    }

    currentProblem() {
      return this.problems.find((problem) => problem.handle === this.selectedProblemHandle) || this.problems[0];
    }

    problemImages(problem) {
      const images = [];
      const addImage = (src, alt) => {
        if (!src) return;
        const normalizedSrc = String(src);
        if (images.some((image) => image.src === normalizedSrc)) return;
        images.push({
          src: normalizedSrc,
          alt: alt || problem.name || 'Problem image'
        });
      };

      addImage(problem.image, problem.imageAlt || problem.name);

      const galleryImages = Array.isArray(problem.galleryImages) ? problem.galleryImages : [];
      galleryImages.forEach((image) => {
        if (typeof image === 'string') {
          addImage(image, problem.name);
        } else if (image && typeof image === 'object') {
          addImage(image.src || image.url, image.alt || problem.name);
        }
      });

      return images;
    }

    renderSelectedProblem() {
      if (!this.selectedWrap) return;
      const problem = this.currentProblem();
      if (!problem) return;

      const desktopBlocks = this.primaryInfoBlocks(problem).map((block) => `
        <div class="pi-info-block">
          <h3 class="pi-info-block__heading">${this.icon(block.icon)} ${this.escapeHtml(block.title)}</h3>
          <div class="pi-info-block__text rte">${block.content}</div>
        </div>
      `).join('');

      const supportBlocks = this.supportInfoBlocks(problem).map((block) => `
        <div class="pi-detail-note">
          <h3 class="pi-info-block__heading">${this.icon(block.icon)} ${this.escapeHtml(block.title)}</h3>
          <div class="pi-info-block__text rte">${block.content}</div>
        </div>
      `).join('');

      const accordionBlocks = this.infoBlocks(problem).map((block, index) => `
        <details ${index === 0 ? 'open' : ''}>
          <summary>${this.icon(block.icon)} <span>${this.escapeHtml(block.title)}</span></summary>
          <div class="pi-info-accordion__text rte">${block.content}</div>
        </details>
      `).join('');

      const images = this.problemImages(problem);
      const primaryImage = images[0];
      const image = primaryImage
        ? `
          <div class="pi-selected-media__main">
            <button type="button" data-pi-zoom="${this.escapeAttr(primaryImage.src)}" aria-label="Zoom ${this.escapeAttr(primaryImage.alt || problem.name)} image">
              <img data-pi-main-img src="${this.escapeAttr(primaryImage.src)}" alt="${this.escapeAttr(primaryImage.alt || problem.name)}" loading="lazy">
            </button>
          </div>
          ${images.length > 1 ? `
            <div class="pi-selected-gallery" aria-label="${this.escapeAttr(problem.name)} image gallery">
              ${images.map((galleryImage, index) => `
                <button
                  type="button"
                  class="pi-selected-gallery__thumb${index === 0 ? ' is-active' : ''}"
                  data-pi-gallery-thumb
                  data-pi-gallery-src="${this.escapeAttr(galleryImage.src)}"
                  data-pi-gallery-alt="${this.escapeAttr(galleryImage.alt || problem.name)}"
                  aria-label="Show ${this.escapeAttr(galleryImage.alt || problem.name)} image"
                  aria-pressed="${index === 0 ? 'true' : 'false'}"
                >
                  <img src="${this.escapeAttr(galleryImage.src)}" alt="" loading="lazy">
                </button>
              `).join('')}
            </div>
          ` : ''}
        `
        : `<div class="pi-problem-card__placeholder">${this.escapeHtml((problem.name || '?').slice(0, 1))}</div>`;

      this.selectedWrap.innerHTML = `
        <article class="pi-selected-card" data-current-problem="${this.escapeAttr(problem.handle)}">
          <div class="pi-selected-media">
            ${image}
          </div>
          <div class="pi-selected-content">
            <div class="pi-selected-title-row">
              <h2 class="pi-selected-title">${this.escapeHtml(problem.name)}</h2>
              ${problem.badge ? `<span class="pi-pill">${this.escapeHtml(problem.badge)}</span>` : ''}
            </div>
            ${problem.scientificName ? `<p class="pi-selected-scientific">${this.escapeHtml(problem.scientificName)}</p>` : ''}
            ${problem.shortDescription ? `<p class="pi-selected-description">${this.escapeHtml(problem.shortDescription)}</p>` : ''}
            ${desktopBlocks ? `<div class="pi-info-grid pi-info-grid--primary">${desktopBlocks}</div>` : ''}
            ${supportBlocks ? `<div class="pi-detail-notes-grid">${supportBlocks}</div>` : ''}
            <div class="pi-info-accordion">${accordionBlocks}</div>
          </div>
        </article>
      `;

      const mainImg = this.selectedWrap.querySelector('[data-pi-main-img]');
      const zoomButton = this.selectedWrap.querySelector('[data-pi-zoom]');
      const thumbnails = Array.from(this.selectedWrap.querySelectorAll('[data-pi-gallery-thumb]'));

      thumbnails.forEach((button) => {
        button.addEventListener('click', () => {
          const src = button.dataset.piGallerySrc;
          const alt = button.dataset.piGalleryAlt || problem.name;
          if (mainImg && src) {
            mainImg.src = src;
            mainImg.alt = alt;
          }
          if (zoomButton && src) {
            zoomButton.dataset.piZoom = src;
            zoomButton.setAttribute('aria-label', `Zoom ${alt} image`);
          }
          thumbnails.forEach((thumb) => {
            const isActive = thumb === button;
            thumb.classList.toggle('is-active', isActive);
            thumb.setAttribute('aria-pressed', String(isActive));
          });
        });
      });

      this.selectedWrap.querySelectorAll('[data-pi-zoom]').forEach((button) => {
        button.addEventListener('click', () => {
          const alt = mainImg ? mainImg.alt : (problem.imageAlt || problem.name);
          this.openImageDialog(button.dataset.piZoom, alt);
        });
      });
    }

    primaryInfoBlocks(problem) {
      const blocks = [];
      if (problem.whatItLooksLike) blocks.push({ icon: 'looks', title: 'What it looks like', content: problem.whatItLooksLike });
      if (problem.whyItHappens) blocks.push({ icon: 'spread', title: 'Why it spreads', content: problem.whyItHappens });
      if (problem.howToPrevent) blocks.push({ icon: 'prevent', title: 'How to prevent it', content: problem.howToPrevent });
      if (problem.howToControl) blocks.push({ icon: 'control', title: 'How to control it', content: problem.howToControl });
      return blocks;
    }

    supportInfoBlocks(problem) {
      const blocks = [];
      if (problem.writtenSolution) blocks.push({ icon: 'solution', title: 'Solution', content: problem.writtenSolution });
      if (problem.warnings) blocks.push({ icon: 'warning', title: 'Important notes', content: problem.warnings });
      return blocks;
    }

    infoBlocks(problem) {
      return [...this.primaryInfoBlocks(problem), ...this.supportInfoBlocks(problem)];
    }

    syncLawnButtons() {
      this.lawnButtons.forEach((button) => {
        button.classList.toggle('is-active', this.selectedLawnTypes.includes(button.dataset.piLawnType));
      });
      if (this.notSureButton) this.notSureButton.classList.toggle('is-active', this.notSureSelected);
    }

    renderSolutions() {
      if (!this.results) return;
      const problem = this.currentProblem();

      if (this.notSureSelected) {
        this.results.innerHTML = this.messageState(this.settings.notSureHeading, this.settings.notSureText, true);
        return;
      }

      if (!this.selectedLawnTypes.length) {
        this.results.innerHTML = this.emptyState(this.settings.emptyProductsHeading, this.settings.emptyProductsText);
        return;
      }

      const cards = Array.from(this.solutionPool ? this.solutionPool.querySelectorAll('[data-pi-solution-card]') : []);
      const matches = cards.filter((card) => {
        const problemHandles = (card.dataset.problemHandles || card.dataset.problemHandle || '').split('|').filter(Boolean);
        if (!problemHandles.includes(problem.handle)) return false;

        const showAllLawnTypes = card.dataset.showAllLawnTypes === 'true';
        if (showAllLawnTypes) return true;

        const lawnTypes = (card.dataset.lawnTypes || '').split('|').filter(Boolean);
        if (!lawnTypes.length) return false;
        return this.selectedLawnTypes.every((selected) => lawnTypes.includes(selected));
      }).sort((a, b) => Number(a.dataset.sortOrder || 99) - Number(b.dataset.sortOrder || 99));

      if (!matches.length) {
        this.results.innerHTML = this.messageState(this.settings.noProductsHeading, this.settings.noProductsText, true);
        return;
      }

      const groupForCard = (card) => {
        const explicitType = (card.dataset.solutionType || '').trim();
        const explicitLabel = (card.dataset.solutionTypeLabel || '').trim();
        const controlHandles = (card.dataset.controlProblemHandles || '').split('|').filter(Boolean);
        const preventionHandles = (card.dataset.preventionProblemHandles || '').split('|').filter(Boolean);
        const controlsSelectedProblem = controlHandles.includes(problem.handle);
        const preventsSelectedProblem = preventionHandles.includes(problem.handle);

        // Prefer the relationship that actually matched the selected problem.
        // This prevents a control product from falling into the fallback "Recommended" group
        // when it has both controls/prevents data or a blank/mismatched solution type field.
        if (controlsSelectedProblem && !preventsSelectedProblem) {
          return { key: 'control', label: 'Control' };
        }
        if (preventsSelectedProblem && !controlsSelectedProblem) {
          return { key: 'prevention', label: 'Prevention' };
        }
        if (controlsSelectedProblem && preventsSelectedProblem) {
          if (explicitType && explicitType !== 'recommended') {
            return { key: explicitType, label: explicitLabel || this.titleFromHandle(explicitType) };
          }
          return { key: 'control', label: 'Control' };
        }
        if (explicitType && explicitType !== 'recommended') {
          return { key: explicitType, label: explicitLabel || this.titleFromHandle(explicitType) };
        }
        return { key: 'recommended', label: 'Recommended Products' };
      };

      const groups = matches.reduce((acc, card) => {
        const group = groupForCard(card);
        if (!acc[group.key]) acc[group.key] = { label: group.label, cards: [] };
        acc[group.key].cards.push(card);
        return acc;
      }, {});

      const lawnLabel = this.selectedLawnTypes.map((type) => this.lawnLabel(type)).join(' + ');
      const heading = `${this.escapeHtml(this.settings.productHeadingPrefix || 'Solutions for')} ${this.escapeHtml(problem.name)} in ${this.escapeHtml(lawnLabel)} Lawns`;

      const productLimit = Number(this.settings.initialProductLimit || 3);
      const groupOrder = ['prevention', 'control', 'spot-treatment-only', 'non-selective', 'recovery', 'recommended'];
      const orderedGroupKeys = Object.keys(groups).sort((a, b) => {
        const aIndex = groupOrder.indexOf(a);
        const bIndex = groupOrder.indexOf(b);
        return (aIndex === -1 ? 99 : aIndex) - (bIndex === -1 ? 99 : bIndex);
      });

      const html = orderedGroupKeys.map((key) => {
        const group = groups[key];
        const extraCount = Math.max(group.cards.length - productLimit, 0);
        const cardsHtml = group.cards.map((card, index) => {
          const clone = card.cloneNode(true);
          if (index >= productLimit) {
            clone.classList.add('is-extra-product');
            clone.setAttribute('hidden', '');
          }
          return clone.outerHTML;
        }).join('');

        return `
          <section class="pi-products-group" data-pi-products-group>
            <div class="pi-products-group__header">
              <h3 class="pi-products-group__title">${this.escapeHtml(group.label)}</h3>
              ${group.cards.length > productLimit ? `<p class="pi-products-group__count">Showing ${productLimit} of ${group.cards.length}</p>` : ''}
            </div>
            <div class="pi-products-grid">${cardsHtml}</div>
            ${extraCount > 0 ? `
              <div class="pi-products-toggle-wrap">
                <button class="pi-products-toggle" type="button" data-pi-products-toggle data-extra-count="${extraCount}" aria-expanded="false">
                  View ${extraCount} more product${extraCount === 1 ? '' : 's'}
                </button>
              </div>
            ` : ''}
          </section>
        `;
      }).join('');

      this.results.innerHTML = `<h3 class="pi-products-heading">${heading}</h3>${html}`;
    }

    renderFaq() {
      if (!this.faq) return;
      const problem = this.currentProblem();
      const faqs = (problem && problem.faqs) || [];
      if (!faqs.length) {
        this.faq.innerHTML = '';
        return;
      }

      this.faq.innerHTML = `
        <h2 class="pi-faq__heading">3. ${this.escapeHtml(this.settings.faqHeading || 'Frequently Asked Questions')}</h2>
        <div class="pi-faq__grid">
          ${faqs.map((faq) => `
            <details>
              <summary>${this.escapeHtml(faq.question)}</summary>
              <div class="pi-faq__answer rte">${faq.answer}</div>
            </details>
          `).join('')}
        </div>
      `;
    }

    renderNoProblems() {
      const shell = this.root.querySelector('.pi-shell');
      if (!shell) return;
      shell.innerHTML = `
        <div class="pi-message-state">
          <div class="pi-message-state__icon">!</div>
          <div>
            <h3>No Lawn Problem metaobjects found.</h3>
            <p>Create Lawn Problem entries with the matching problem_type value for this section.</p>
          </div>
        </div>
      `;
    }

    emptyState(heading, text) {
      return `
        <div class="pi-empty-state">
          <div class="pi-empty-state__icon" aria-hidden="true">${this.icon('solution')}</div>
          <div>
            <h3>${this.escapeHtml(heading || '')}</h3>
            <p>${this.escapeHtml(text || '')}</p>
          </div>
        </div>
      `;
    }

    messageState(heading, text, includeButton) {
      return `
        <div class="pi-message-state">
          <div class="pi-message-state__icon" aria-hidden="true">${this.icon('warning')}</div>
          <div>
            <h3>${this.escapeHtml(heading || '')}</h3>
            <p>${this.escapeHtml(text || '')}</p>
            ${includeButton && this.settings.contactUrl ? `<a class="pi-message-state__button" href="${this.escapeAttr(this.settings.contactUrl)}">${this.escapeHtml(this.settings.contactButtonLabel || 'Contact Us')}</a>` : ''}
          </div>
        </div>
      `;
    }

    scrollProblems(direction) {
      if (!this.problemList) return;
      const amount = this.problemList.clientWidth * 0.72;
      this.problemList.scrollBy({ left: amount * direction, behavior: 'smooth' });
    }

    openImageDialog(src, alt) {
      if (!this.settings.imageZoomEnabled || !this.imageDialog || !this.imageDialogImg) return;
      this.imageDialogImg.src = src;
      this.imageDialogImg.alt = alt || '';
      if (typeof this.imageDialog.showModal === 'function') {
        this.imageDialog.showModal();
      }
    }

    closeImageDialog() {
      if (!this.imageDialog || !this.imageDialog.open) return;
      this.imageDialog.close();
      if (this.imageDialogImg) this.imageDialogImg.src = '';
    }

    icon(name) {
      const url = this.settings.icons && this.settings.icons[name];
      if (url) return `<span class="pi-info-icon"><img src="${this.escapeAttr(url)}" alt="" loading="lazy"></span>`;

      const svgs = {
        looks: '<svg viewBox="0 0 24 24" role="presentation"><path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" fill="none" stroke="currentColor" stroke-width="1.7"/><circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" stroke-width="1.7"/></svg>',
        spread: '<svg viewBox="0 0 24 24" role="presentation"><circle cx="6" cy="7" r="2.4" fill="none" stroke="currentColor" stroke-width="1.7"/><circle cx="18" cy="6" r="2.4" fill="none" stroke="currentColor" stroke-width="1.7"/><circle cx="17" cy="18" r="2.4" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M8.2 7h7.4M7.8 8.8l7.4 7.4" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>',
        prevent: '<svg viewBox="0 0 24 24" role="presentation"><path d="M12 3 19 6v5.5c0 4.8-3 7.8-7 9.5-4-1.7-7-4.7-7-9.5V6l7-3Z" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="m8.7 12 2.2 2.2 4.7-5" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>',
        control: '<svg viewBox="0 0 24 24" role="presentation"><path d="M4 19c8-1 13-6 16-15-8 1-14 6-16 15Z" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M4 19c4-4 8-6 13-8" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>',
        solution: '<svg viewBox="0 0 24 24" role="presentation"><path d="M12 21V5M7 21c1-6 0-10-2-14 5 4 6 9 5 14M13 21c1-8 4-13 8-17-1 8-3 13-6 17" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>',
        warning: '<svg viewBox="0 0 24 24" role="presentation"><path d="M12 3 22 20H2L12 3Z" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M12 9v5M12 17h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>'
      };

      return `<span class="pi-info-icon">${svgs[name] || svgs.solution}</span>`;
    }

    lawnLabel(handle) {
      const labels = {
        bermuda: 'Bermuda',
        zoysia: 'Zoysia',
        'st-augustine': 'St. Augustine',
        centipede: 'Centipede',
        'tall-fescue': 'Tall Fescue',
        'northern-cool-season': 'Northern Cool-Season'
      };
      return labels[handle] || handle.replace(/-/g, ' ');
    }

    escapeHtml(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }


    titleFromHandle(value) {
      return String(value || '')
        .split('-')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
    }

    escapeAttr(value) {
      return this.escapeHtml(value).replace(/`/g, '&#096;');
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-problem-identifier]').forEach((root) => new ProblemIdentifier(root));
  });
})();
